import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    sendPasswordResetEmail,
    signOut
} from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { auth, database } from '../firebase';

// ─── Mapeo de planes Stripe → productId de Cloud Function ─────────────────────
// La Cloud Function grantTestSubscription espera productId en este formato
const STRIPE_PLANS = {
    annual: {
        id: 'annual',
        productId: 'subs_365d',           // ← igual que la app Android
        name: 'Anual',
        label: 'Anual',
        price: 1900,
        duration: '12 meses',
        durationDays: 365,
        description: 'Acceso premium por 12 meses',
        stripeUrl: 'https://buy.stripe.com/5kQ4gz9qg3Gieo2cLD73G0a',
        icon: '💎',
        badge: 'Mejor Valor',
        badgeColor: '#c5a059',
        recommended: false,
        savings: 'Ahorra $1100 vs mensual',
    },
    semiannual: {
        id: 'semiannual',
        productId: 'subs_180d',
        name: 'Semestral',
        label: 'Semestral',
        price: 1100,
        duration: '6 meses',
        durationDays: 180,
        description: 'Acceso premium por 6 meses',
        stripeUrl: 'https://buy.stripe.com/14AbJ1cCsekW2FkaDv73G09',
        icon: '📆',
        badge: 'Conveniente',
        badgeColor: '#dbb96a',
        recommended: false,
        savings: '',
    },
    biannual: {
        id: 'biannual',
        productId: 'subs_60d',
        name: 'Bimestral',
        label: 'Bimestral',
        price: 450,
        duration: '2 meses',
        durationDays: 60,
        description: 'Acceso premium por 2 meses',
        stripeUrl: 'https://buy.stripe.com/4gMaEX9qg0u63Jo5jb73G08',
        icon: '🗓️',
        badge: 'Popular',
        badgeColor: '#c5a059',
        recommended: true,
        savings: '',
    },
    monthly: {
        id: 'monthly',
        productId: 'subs_30d',
        name: 'Mensual',
        label: 'Mensual',
        price: 250,
        duration: '1 mes',
        durationDays: 30,
        description: 'Acceso premium por 1 mes',
        stripeUrl: 'https://buy.stripe.com/5kQbJ16e4gt4fs64f773G07',
        icon: '📅',
        badge: 'Básico',
        badgeColor: '#7a8fa0',
        recommended: false,
        savings: '',
    },
};

const PLAN_ORDER = ['annual', 'semiannual', 'biannual', 'monthly'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const encodeEmail = (email) =>
    email.toLowerCase().replace(/\./g, '_dot_').replace(/@/g, '_at_');

// ─── Componente Principal ─────────────────────────────────────────────────────
const PurchaseLicense = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialPlan = searchParams.get('plan') || 'biannual';

    // ── Autenticación ──
    const [authStep, setAuthStep]           = useState('login'); // 'login' | 'register' | 'forgot'
    const [authenticatedUser, setAuthenticatedUser] = useState(null);
    const [authChecked, setAuthChecked]     = useState(false);

    // ── Formulario ──
    const [email, setEmail]                 = useState('');
    const [password, setPassword]           = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword]   = useState(false);
    const [authLoading, setAuthLoading]     = useState(false);
    const [authError, setAuthError]         = useState(null);
    const [resetSent, setResetSent]         = useState(false);

    // ── Planes ──
    const [selectedPlan, setSelectedPlan]   = useState(
        STRIPE_PLANS[initialPlan] ? initialPlan : 'biannual'
    );
    const [payLoading, setPayLoading]       = useState(false);

    // ─── Escuchar sesión al montar ────────────────────────────────────────────
    useEffect(() => {
        const unsub = auth.onAuthStateChanged((user) => {
            if (user) {
                setAuthenticatedUser(user);
                if (user.email) setEmail(user.email);
            } else {
                setAuthenticatedUser(null);
            }
            setAuthChecked(true);
        });
        return () => unsub();
    }, []);

    // ─── Manejo de autenticación ──────────────────────────────────────────────
    const getAuthErrorMessage = (code) => {
        const messages = {
            'auth/invalid-email':       'Correo electrónico inválido.',
            'auth/invalid-credential':  'Correo o contraseña incorrectos.',
            'auth/wrong-password':      'Contraseña incorrecta.',
            'auth/user-not-found':      'No existe una cuenta con ese correo.',
            'auth/email-already-in-use':'Este correo ya tiene una cuenta. Inicia sesión en su lugar.',
            'auth/too-many-requests':   'Demasiados intentos. Espera unos minutos.',
            'auth/weak-password':       'La contraseña debe tener al menos 6 caracteres.',
            'auth/network-request-failed': 'Sin conexión. Verifica tu internet.',
            'auth/popup-closed-by-user':'El popup fue cerrado. Intenta de nuevo.',
            'auth/popup-blocked':       'El popup fue bloqueado. Permite popups para este sitio.',
        };
        return messages[code] || 'Ocurrió un error inesperado. Intenta de nuevo.';
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setAuthError(null);
        if (!email || !password) { setAuthError('Completa todos los campos.'); return; }
        if (authStep === 'register' && password !== confirmPassword) {
            setAuthError('Las contraseñas no coinciden.'); return;
        }
        if (password.length < 6) { setAuthError('Mínimo 6 caracteres.'); return; }

        setAuthLoading(true);
        try {
            if (authStep === 'register') {
                await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
            } else {
                await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
            }
            // onAuthStateChanged actualizará authenticatedUser automáticamente
        } catch (err) {
            // Si intenta registrarse y el correo ya existe → sugerir login
            if (err.code === 'auth/email-already-in-use') {
                setAuthError('Este correo ya tiene cuenta. Por favor usa "Ya tengo cuenta" para iniciar sesión.');
                setAuthStep('login');
            } else {
                setAuthError(getAuthErrorMessage(err.code));
            }
        } finally {
            setAuthLoading(false);
        }
    };

    const handleGoogleAuth = async () => {
        setAuthError(null);
        setAuthLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');
            provider.setCustomParameters({ prompt: 'select_account' });
            await signInWithPopup(auth, provider);
            // onAuthStateChanged actualizará authenticatedUser automáticamente
        } catch (err) {
            setAuthError(getAuthErrorMessage(err.code));
        } finally {
            setAuthLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setAuthError(null);
        setResetSent(false);
        if (!email) { setAuthError('Escribe tu correo para restablecer la contraseña.'); return; }
        setAuthLoading(true);
        try {
            await sendPasswordResetEmail(auth, email.trim().toLowerCase());
            setResetSent(true);
        } catch (err) {
            setAuthError(getAuthErrorMessage(err.code));
        } finally {
            setAuthLoading(false);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        setPassword('');
        setConfirmPassword('');
        setAuthError(null);
    };

    // ─── Redirigir a Stripe ───────────────────────────────────────────────────
    const handlePayWithStripe = async (planId) => {
        if (!authenticatedUser) return;
        setPayLoading(true);
        const plan = STRIPE_PLANS[planId];
        const user = authenticatedUser;
        const encodedEmailKey = encodeEmail(user.email);
        const now = Date.now();

        try {
            // Guardar en usersweb (datos de sesión web)
            await set(ref(database, `usersweb/${user.uid}`), {
                email: user.email,
                displayName: user.displayName || user.email.split('@')[0],
                updatedAt: now,
                pendingStripePayment: {
                    planId: plan.id,
                    productId: plan.productId,   // ← productId compatible con Cloud Function
                    planName: plan.name,
                    amount: plan.price,
                    durationDays: plan.durationDays,
                    initiatedAt: now,
                    status: 'pending_payment',
                },
            });

            // Guardar en stripe_pending (para el webhook de Stripe)
            // Usa el mismo productId que grantTestSubscription espera
            await set(ref(database, `stripe_pending/${encodedEmailKey}`), {
                uid: user.uid,
                email: user.email,
                planId: plan.id,
                productId: plan.productId,       // ← 'subs_30d' | 'subs_60d' | etc.
                planName: plan.name,
                amount: plan.price,
                durationDays: plan.durationDays,
                initiatedAt: now,
                status: 'pending',
            });

            // Redirigir a Stripe con prefill
            const stripeUrl = `${plan.stripeUrl}?client_reference_id=${user.uid}&prefilled_email=${encodeURIComponent(user.email)}`;
            window.location.href = stripeUrl;
        } catch (err) {
            console.error('[PurchaseLicense] Error guardando pago pendiente:', err);
            setPayLoading(false);
        }
    };

    // ─── Render helpers ───────────────────────────────────────────────────────
    if (!authChecked) {
        return (
            <div style={S.page}>
                <style>{`
                    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                `}</style>
                <div style={S.loadingBox}>
                    <div style={S.spinner} />
                    <p style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>Verificando sesión...</p>
                </div>
            </div>
        );
    }


    return (
        <div style={S.page}>
            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                #auth-email:focus, #auth-password:focus, #auth-confirm-password:focus {
                    border-color: var(--gold-matte) !important;
                    box-shadow: 0 0 0 3px rgba(197,160,89,0.15);
                }
            `}</style>
            {/* ── Header ── */}
            <header style={S.header}>
                <div style={S.headerInner}>
                    <div onClick={() => navigate('/')} style={S.logo}>
                        <img src="/icons/logo.png" alt="AscensosApp Vortex" style={{ height: '32px' }} />
                        <span style={S.logoText}>AscensosApp Vortex</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        {authenticatedUser && (
                            <span style={S.userBadge}>
                                ✅ {authenticatedUser.email}
                            </span>
                        )}
                        <button onClick={() => navigate('/')} style={S.backBtn}>← Inicio</button>
                        {authenticatedUser && (
                            <button onClick={handleLogout} style={S.logoutBtn}>Salir</button>
                        )}
                    </div>
                </div>
            </header>

            <main style={{ paddingTop: '80px' }}>
                {/* ── Hero ── */}
                <section style={S.hero}>
                    <div style={S.heroTag}>
                        <span>⚡</span>
                        <span>Pago seguro con Stripe</span>
                    </div>
                    <h1 style={S.heroTitle}>
                        {authenticatedUser ? 'Elige tu Plan Premium' : 'Accede para Adquirir tu Suscripción Web'}
                    </h1>
                    <p style={S.heroSub}>
                        {authenticatedUser
                            ? 'Acceso completo a simuladores, quizzes con IA y material de estudio naval'
                            : 'Inicia sesión o crea una cuenta para ver y adquirir tu suscripción web'}
                    </p>
                </section>

                {/* ═══════════════════════════════════════
                    BLOQUE A: AUTENTICACIÓN (si no hay sesión)
                    ═══════════════════════════════════════ */}
                {!authenticatedUser && (
                    <section style={S.authSection}>
                        <div style={S.authCard}>
                            {/* Título del formulario */}
                            <div style={S.authCardHeader}>
                                <div style={S.authIcon}>
                                    {authStep === 'forgot' ? '🔑' : authStep === 'register' ? '✨' : '🔐'}
                                </div>
                                <h2 style={S.authTitle}>
                                    {authStep === 'forgot'
                                        ? 'Restablecer Contraseña'
                                        : authStep === 'register'
                                        ? 'Crear Cuenta Nueva'
                                        : 'Iniciar Sesión'}
                                </h2>
                                <p style={S.authSubtitle}>
                                    {authStep === 'forgot'
                                        ? 'Te enviaremos un enlace de recuperación a tu correo'
                                        : authStep === 'register'
                                        ? 'Crea tu cuenta para continuar con tu compra'
                                        : 'Accede con tu cuenta existente'}
                                </p>
                            </div>

                            {/* Toggle Nuevo / Existente */}
                            {authStep !== 'forgot' && (
                                <div style={S.toggle}>
                                    <button
                                        style={{ ...S.toggleBtn, ...(authStep === 'login' ? S.toggleActive : {}) }}
                                        onClick={() => { setAuthStep('login'); setAuthError(null); setResetSent(false); }}
                                    >
                                        Ya tengo cuenta
                                    </button>
                                    <button
                                        style={{ ...S.toggleBtn, ...(authStep === 'register' ? S.toggleActive : {}) }}
                                        onClick={() => { setAuthStep('register'); setAuthError(null); setResetSent(false); }}
                                    >
                                        Soy nuevo usuario
                                    </button>
                                </div>
                            )}

                            {/* Error */}
                            {authError && (
                                <div style={S.errorBox}>⚠️ {authError}</div>
                            )}

                            {/* Éxito reset */}
                            {resetSent && (
                                <div style={S.successBox}>
                                    ✅ Correo enviado. Revisa tu bandeja de entrada y sigue el enlace para restablecer tu contraseña.
                                </div>
                            )}

                            {/* ── Formulario ── */}
                            {authStep === 'forgot' ? (
                                <form onSubmit={handleForgotPassword} style={S.form}>
                                    <div style={S.formGroup}>
                                        <label style={S.label}>Correo Electrónico</label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            placeholder="tu@correo.com"
                                            required
                                            autoComplete="email"
                                            style={S.input}
                                        />
                                    </div>
                                    <button type="submit" disabled={authLoading} style={S.primaryBtn}>
                                        {authLoading ? 'Enviando...' : '📧 Enviar enlace de recuperación'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setAuthStep('login'); setAuthError(null); setResetSent(false); }}
                                        style={S.ghostBtn}
                                    >
                                        ← Volver al inicio de sesión
                                    </button>
                                </form>
                            ) : (
                                <form onSubmit={handleEmailAuth} style={S.form}>
                                    {/* Email */}
                                    <div style={S.formGroup}>
                                        <label style={S.label}>Correo Electrónico</label>
                                        <input
                                            id="auth-email"
                                            type="email"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            placeholder="tu@correo.com"
                                            required
                                            autoComplete="email"
                                            style={S.input}
                                        />
                                    </div>

                                    {/* Contraseña */}
                                    <div style={S.formGroup}>
                                        <label style={S.label}>
                                            {authStep === 'register' ? 'Crea una Contraseña' : 'Tu Contraseña'}
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                id="auth-password"
                                                type={showPassword ? 'text' : 'password'}
                                                value={password}
                                                onChange={e => setPassword(e.target.value)}
                                                placeholder={authStep === 'register' ? 'Mínimo 6 caracteres' : '••••••••'}
                                                required
                                                autoComplete={authStep === 'register' ? 'new-password' : 'current-password'}
                                                style={{ ...S.input, paddingRight: '48px' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(v => !v)}
                                                style={S.eyeBtn}
                                            >
                                                {showPassword ? '🙈' : '👁️'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confirmar contraseña (solo registro) */}
                                    {authStep === 'register' && (
                                        <div style={S.formGroup}>
                                            <label style={S.label}>Confirmar Contraseña</label>
                                            <input
                                                id="auth-confirm-password"
                                                type={showPassword ? 'text' : 'password'}
                                                value={confirmPassword}
                                                onChange={e => setConfirmPassword(e.target.value)}
                                                placeholder="Repite tu contraseña"
                                                required
                                                autoComplete="new-password"
                                                style={S.input}
                                            />
                                        </div>
                                    )}

                                    {/* Olvidé contraseña (solo login) */}
                                    {authStep === 'login' && (
                                        <button
                                            type="button"
                                            onClick={() => { setAuthStep('forgot'); setAuthError(null); setResetSent(false); }}
                                            style={S.forgotLink}
                                        >
                                            ¿Olvidaste tu contraseña?
                                        </button>
                                    )}

                                    {/* Nota privacidad */}
                                    <div style={S.privacyNote}>
                                        🔒 Tu correo se usará para activar automáticamente tu suscripción tras el pago con Stripe.
                                    </div>

                                    {/* Botón principal */}
                                    <button id="btn-auth-submit" type="submit" disabled={authLoading} style={S.primaryBtn}>
                                        {authLoading
                                            ? 'Procesando...'
                                            : authStep === 'register'
                                            ? '✨ Crear cuenta y continuar'
                                            : '🔐 Iniciar sesión y continuar'}
                                    </button>
                                </form>
                            )}

                            {/* ── Separador Google ── */}
                            {authStep !== 'forgot' && (
                                <>
                                    <div style={S.separator}>
                                        <div style={S.separatorLine} />
                                        <span style={S.separatorText}>O ACCEDE CON</span>
                                        <div style={S.separatorLine} />
                                    </div>

                                    <button
                                        id="btn-google-auth"
                                        onClick={handleGoogleAuth}
                                        disabled={authLoading}
                                        style={S.googleBtn}
                                    >
                                        <img
                                            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                            alt="Google"
                                            width="20"
                                            style={{ flexShrink: 0 }}
                                        />
                                        <span>Acceso con Google</span>
                                    </button>

                                    <p style={S.googleNote}>
                                        Ideal si usas la app Android con tu cuenta de Google
                                    </p>
                                </>
                            )}
                        </div>
                    </section>
                )}

                {/* ═══════════════════════════════════════
                    BLOQUE B: PLANES (si hay sesión activa)
                    ═══════════════════════════════════════ */}
                {authenticatedUser && (
                    <>
                        {/* Bienvenida */}
                        <section style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 2rem 1rem' }}>
                            <div style={S.welcomeBanner}>
                                <span style={{ fontSize: '1.5rem' }}>👋</span>
                                <div>
                                    <p style={{ margin: 0, color: '#4caf50', fontWeight: '700', fontSize: '0.95rem' }}>
                                        Sesión iniciada como
                                    </p>
                                    <p style={{ margin: 0, color: '#f5f5f5', fontSize: '0.9rem' }}>
                                        {authenticatedUser.displayName || authenticatedUser.email}
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Planes */}
                        <section style={S.plansSection}>
                            <h2 style={S.plansSectionTitle}>Elige tu plan</h2>
                            <div style={S.plansGrid}>
                                {PLAN_ORDER.map((planId) => {
                                    const plan = STRIPE_PLANS[planId];
                                    const isSelected = selectedPlan === planId;
                                    return (
                                        <div
                                            key={planId}
                                            id={`plan-card-${planId}`}
                                            onClick={() => setSelectedPlan(planId)}
                                            style={{
                                                ...S.planCard,
                                                ...(isSelected ? S.planCardSelected : {}),
                                                ...(plan.recommended ? S.planCardRecommended : {}),
                                            }}
                                            onMouseEnter={e => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                                    e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.35)';
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }
                                            }}
                                        >
                                            {plan.recommended && (
                                                <div style={S.recommendedBadge}>⭐ Recomendado</div>
                                            )}
                                            <div style={S.planBadge(plan.badgeColor)}>{plan.badge}</div>
                                            <div style={S.planIcon}>{plan.icon}</div>
                                            <h3 style={S.planName}>{plan.label}</h3>
                                            <div style={S.planPriceRow}>
                                                <span style={S.priceAmount}>${plan.price}</span>
                                                <span style={S.priceCurrency}> MXN</span>
                                            </div>
                                            <p style={S.planDuration}>{plan.duration}</p>
                                            {plan.savings && <p style={S.planSavings}>{plan.savings}</p>}
                                            {isSelected && <div style={S.selectedBadge}>✓ Seleccionado</div>}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Botón de pago */}
                            <div style={{ maxWidth: '480px', margin: '0 auto' }}>
                                <button
                                    id="btn-pay-stripe"
                                    onClick={() => handlePayWithStripe(selectedPlan)}
                                    disabled={payLoading}
                                    style={{
                                        ...S.stripeBtn,
                                        ...(payLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                                    }}
                                    onMouseEnter={e => { if (!payLoading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(197,160,89,0.5)'; }}}
                                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(197,160,89,0.35)'; }}
                                >
                                    {payLoading ? (
                                        <span>Redirigiendo...</span>
                                    ) : (
                                        <>
                                            <span>💳 Pagar con Stripe</span>
                                            <span style={{ fontSize: '0.85rem', opacity: 0.85 }}>
                                                — {STRIPE_PLANS[selectedPlan].label} ${STRIPE_PLANS[selectedPlan].price} MXN
                                            </span>
                                        </>
                                    )}
                                </button>
                                <p style={S.stripeSecureNote}>
                                    🔐 Pago 100% seguro procesado por <strong>Stripe</strong> · No almacenamos datos de tarjeta
                                </p>
                            </div>
                        </section>

                        {/* Qué incluye */}
                        <section style={S.featuresSection}>
                            <h2 style={S.featuresTitle}>¿Qué incluye tu suscripción?</h2>
                            <div style={S.featuresGrid}>
                                {[
                                    { icon: '🎯', title: 'Simuladores de Examen', desc: 'Practica con exámenes reales de ascensos navales' },
                                    { icon: '🤖', title: 'Quizzes con IA', desc: 'Generación automática de preguntas con Inteligencia Artificial' },
                                    { icon: '📚', title: 'Material de Estudio', desc: 'Acceso a toda la biblioteca de material naval' },
                                    { icon: '🎵', title: 'Audioteca', desc: 'Leyes y reglamentos en formato de audio' },
                                ].map((f, i) => (
                                    <div key={i} style={S.featureCard}>
                                        <div style={S.featureIcon}>{f.icon}</div>
                                        <h3 style={S.featureTitle}>{f.title}</h3>
                                        <p style={S.featureDesc}>{f.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Comparativa */}
                        <section style={{ padding: '3rem 2rem', maxWidth: '900px', margin: '0 auto' }}>
                            <h2 style={{ textAlign: 'center', margin: '0 0 2rem 0', fontSize: '1.5rem', color: '#f5f5f5' }}>
                                📊 Compara nuestros planes
                            </h2>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', color: '#f5f5f5', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', overflow: 'hidden' }}>
                                    <thead>
                                        <tr style={{ background: 'rgba(197, 160, 89, 0.18)' }}>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Característica</th>
                                            <th style={{ padding: '1rem', textAlign: 'center' }}>Mensual</th>
                                            <th style={{ padding: '1rem', textAlign: 'center' }}>Bimestral</th>
                                            <th style={{ padding: '1rem', textAlign: 'center' }}>Semestral</th>
                                            <th style={{ padding: '1rem', textAlign: 'center' }}>Anual</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ['Precio', '$250 MXN', '$450 MXN', '$1100 MXN', '$1900 MXN'],
                                            ['Acceso web completo', '✅', '✅', '✅', '✅'],
                                            ['Simuladores de examen', '✅', '✅', '✅', '✅'],
                                            ['Generador de quizzes IA', '✅', '✅', '✅', '✅'],
                                            ['Material de estudio', '✅', '✅', '✅', '✅'],
                                        ].map((row, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                {row.map((cell, j) => (
                                                    <td key={j} style={{ padding: '0.75rem 1rem', textAlign: j === 0 ? 'left' : 'center', color: j === 0 ? '#f5f5f5' : '#4caf50' }}>
                                                        {cell}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        <tr>
                                            <td style={{ padding: '0.75rem 1rem' }}>Ahorro</td>
                                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: 'var(--text-muted)' }}>—</td>
                                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#4caf50' }}>¡Ahorra $50!</td>
                                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#4caf50' }}>¡Ahorra $400!</td>
                                            <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#4caf50' }}>¡Ahorra $1100!</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {/* FAQ */}
                        <section style={{ padding: '3rem 2rem', maxWidth: '800px', margin: '0 auto' }}>
                            <h2 style={{ textAlign: 'center', margin: '0 0 2rem 0', fontSize: '1.5rem', color: '#f5f5f5' }}>❓ Preguntas Frecuentes</h2>
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                {[
                                    { q: '¿Cómo se activa mi suscripción?', a: 'Al completar el pago en Stripe, tu suscripción se activa automáticamente y podrás acceder al dashboard completo.' },
                                    { q: '¿Puedo usar la suscripción web en la app móvil?', a: 'No, la suscripción web es exclusiva para la versión web y no incluye acceso a la aplicación móvil.' },
                                    { q: '¿Qué pasa si ya tenía cuenta en la app?', a: 'Puedes usar el botón "Acceso con Google" si te registraste con Google en la app móvil, y tu correo se vinculará automáticamente.' },
                                    { q: '¿Puedo cancelar mi suscripción?', a: 'Sí. Desde tu Perfil pulsa "Cancelar suscripción" para gestionarla en el portal de Stripe. La devolución aplica solo si escribes a ascensosappvortex@gmail.com dentro de los primeros 7 días; tras ese plazo el acceso queda hasta el final del periodo pagado.' },
                                ].map((faq, i) => (
                                    <div key={i} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '1.25rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#dbb96a', fontSize: '1rem' }}>{faq.q}</h4>
                                        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{faq.a}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </>
                )}
            </main>

            {/* Footer */}
            <footer style={{ padding: '2rem', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '2rem' }}>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    ¿Tienes dudas?{' '}
                    <a href="mailto:ascensosappvortex@gmail.com" style={{ color: 'var(--gold-matte)', textDecoration: 'none' }}>
                        ascensosappvortex@gmail.com
                    </a>
                </p>
                <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    AscensosApp Vortex © {new Date().getFullYear()}
                </p>
            </footer>
        </div>
    );
};

// ─── Estilos ──────────────────────────────────────────────────────────────────
const S = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1a2a 0%, #0f2035 50%, #0a1a2a 100%)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-primary)',
    },
    loadingBox: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh',
    },
    spinner: {
        width: '40px', height: '40px',
        border: '3px solid rgba(197,160,89,0.2)',
        borderTop: '3px solid var(--gold-matte)',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    header: {
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: 'rgba(10,26,42,0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(197,160,89,0.18)',
    },
    headerInner: {
        maxWidth: '1200px', margin: '0 auto', padding: '0.85rem 2rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    logo: { cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' },
    logoText: { fontSize: '1.1rem', fontWeight: '700', color: '#f5f5f5' },
    userBadge: {
        fontSize: '0.75rem', color: '#4caf50', background: 'rgba(76,175,80,0.1)',
        border: '1px solid rgba(76,175,80,0.3)', borderRadius: '20px', padding: '4px 12px',
    },
    backBtn: {
        background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
        padding: '7px 14px', color: 'var(--text-secondary)', borderRadius: '8px',
        fontSize: '0.85rem', cursor: 'pointer',
    },
    logoutBtn: {
        background: 'transparent', border: '1px solid rgba(255,82,82,0.4)',
        padding: '7px 14px', color: '#ff8a8a', borderRadius: '8px',
        fontSize: '0.85rem', cursor: 'pointer',
    },
    hero: {
        textAlign: 'center', padding: '4rem 2rem 2rem',
        background: 'linear-gradient(180deg, rgba(197,160,89,0.12) 0%, transparent 100%)',
    },
    heroTag: {
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        background: 'rgba(197,160,89,0.2)', border: '1px solid rgba(197,160,89,0.4)',
        borderRadius: '20px', padding: '4px 14px', fontSize: '0.8rem', color: '#dbb96a',
        marginBottom: '1rem', fontWeight: '500',
    },
    heroTitle: {
        fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '800',
        background: 'linear-gradient(90deg, #f5f5f5 0%, #dbb96a 100%)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        margin: '0 0 1rem 0',
    },
    heroSub: { color: 'var(--text-secondary)', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto' },

    // ── Auth ──
    authSection: {
        display: 'flex', justifyContent: 'center', padding: '2rem 1.5rem 4rem',
    },
    authCard: {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '24px', padding: '2.5rem 2rem',
        width: '100%', maxWidth: '480px',
        backdropFilter: 'blur(12px)',
    },
    authCardHeader: { textAlign: 'center', marginBottom: '1.75rem' },
    authIcon: { fontSize: '2.5rem', marginBottom: '0.75rem' },
    authTitle: { fontSize: '1.5rem', fontWeight: '800', color: '#f5f5f5', margin: '0 0 0.5rem 0' },
    authSubtitle: { color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 },
    toggle: {
        display: 'flex', borderRadius: '10px', background: 'rgba(0,0,0,0.3)',
        padding: '4px', marginBottom: '1.5rem', gap: '4px',
    },
    toggleBtn: {
        flex: 1, padding: '9px', borderRadius: '8px', border: 'none',
        background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.875rem',
        cursor: 'pointer', fontWeight: '500', transition: 'background-color 0.2s ease, color 0.2s ease',
    },
    toggleActive: { background: 'rgba(197,160,89,0.3)', color: '#dbb96a', fontWeight: '700' },
    errorBox: {
        background: 'rgba(255,82,82,0.15)', border: '1px solid rgba(255,82,82,0.4)',
        borderRadius: '10px', padding: '12px 16px', color: '#ff8a8a',
        fontSize: '0.875rem', marginBottom: '1rem',
    },
    successBox: {
        background: 'rgba(76,175,80,0.12)', border: '1px solid rgba(76,175,80,0.35)',
        borderRadius: '10px', padding: '12px 16px', color: '#a5d6a7',
        fontSize: '0.875rem', marginBottom: '1rem', lineHeight: 1.5,
    },
    form: { display: 'flex', flexDirection: 'column', gap: '1.1rem' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: '600' },
    input: {
        width: '100%', padding: '12px 16px', borderRadius: '10px',
        border: '1.5px solid rgba(255,255,255,0.15)',
        background: 'rgba(255,255,255,0.07)', color: '#f5f5f5',
        fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none',
        transition: 'border-color 0.2s',
    },
    eyeBtn: {
        position: 'absolute', right: '12px', top: '50%',
        transform: 'translateY(-50%)', background: 'none',
        border: 'none', cursor: 'pointer', fontSize: '1rem', color: 'var(--text-secondary)',
    },
    forgotLink: {
        background: 'none', border: 'none', color: '#dbb96a',
        fontSize: '0.82rem', cursor: 'pointer', textAlign: 'left', padding: 0,
        textDecoration: 'underline',
    },
    privacyNote: {
        background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.2)',
        borderRadius: '8px', padding: '10px 14px', color: '#a5d6a7',
        fontSize: '0.78rem', lineHeight: 1.5,
    },
    primaryBtn: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '2px', background: 'linear-gradient(135deg, #c5a059 0%, #dbb96a 100%)',
        border: 'none', padding: '14px 20px', color: '#0a1a2a', borderRadius: '10px',
        fontSize: '1rem', fontWeight: '700', cursor: 'pointer',
        transition: 'opacity 0.2s, box-shadow 0.2s', boxShadow: '0 4px 16px rgba(197,160,89,0.4)',
    },
    ghostBtn: {
        background: 'transparent', border: '1.5px solid rgba(255,255,255,0.2)',
        padding: '12px 20px', color: 'var(--text-secondary)', borderRadius: '10px',
        fontSize: '0.875rem', cursor: 'pointer',
    },
    separator: {
        display: 'flex', alignItems: 'center', gap: '12px', margin: '1.5rem 0 1rem',
    },
    separatorLine: { flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' },
    separatorText: { color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: '600', whiteSpace: 'nowrap' },
    googleBtn: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
        width: '100%', padding: '13px 20px',
        background: 'rgba(255,255,255,0.06)',
        border: '1.5px solid rgba(255,255,255,0.15)',
        borderRadius: '10px', color: '#f5f5f5', fontSize: '0.95rem',
        fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.2s ease, border-color 0.2s ease',
    },
    googleNote: { textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.75rem', margin: '0.75rem 0 0' },

    // ── Planes ──
    welcomeBanner: {
        display: 'flex', alignItems: 'center', gap: '1rem',
        background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.2)',
        borderRadius: '12px', padding: '1rem 1.5rem', marginBottom: '0.5rem',
    },
    plansSection: { maxWidth: '1100px', margin: '0 auto', padding: '1rem 2rem 2rem' },
    plansSectionTitle: {
        fontSize: '1.3rem', fontWeight: '700', color: '#f5f5f5',
        marginBottom: '1.5rem',
    },
    plansGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1.25rem', marginBottom: '2rem',
    },
    planCard: {
        background: 'rgba(255,255,255,0.05)', border: '1.5px solid rgba(255,255,255,0.1)',
        borderRadius: '16px', padding: '1.5rem', cursor: 'pointer',
        position: 'relative', transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.2s',
        textAlign: 'center',
    },
    planCardSelected: {
        border: '2px solid var(--gold-matte)', background: 'rgba(197,160,89,0.15)',
        boxShadow: '0 0 20px rgba(197,160,89,0.25)',
    },
    planCardRecommended: { border: '2px solid var(--gold-matte)', background: 'rgba(197,160,89,0.12)' },
    recommendedBadge: {
        position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)',
        background: 'var(--gold-matte)', color: '#0a1a2a', padding: '4px 14px', borderRadius: '20px',
        fontSize: '0.72rem', fontWeight: '600', whiteSpace: 'nowrap',
    },
    planBadge: (color) => ({
        display: 'inline-block', background: `${color}22`, border: `1px solid ${color}55`,
        color: color, borderRadius: '12px', padding: '2px 10px',
        fontSize: '0.7rem', fontWeight: '600', marginBottom: '0.75rem',
    }),
    planIcon: { fontSize: '2.5rem', marginBottom: '0.5rem' },
    planName: { color: '#f5f5f5', fontSize: '1.15rem', fontWeight: '700', margin: '0 0 0.5rem 0' },
    planPriceRow: { display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px', marginBottom: '0.25rem' },
    priceAmount: { fontSize: '2rem', fontWeight: '800', color: '#f5f5f5' },
    priceCurrency: { color: 'var(--text-muted)', fontSize: '0.85rem' },
    planDuration: { color: 'var(--text-secondary)', fontSize: '0.78rem', margin: '0 0 0.5rem 0' },
    planSavings: { color: '#4caf50', fontSize: '0.75rem', fontWeight: '600', margin: 0 },
    selectedBadge: {
        marginTop: '0.75rem', background: 'rgba(76,175,80,0.2)',
        border: '1px solid #4caf50', borderRadius: '8px', padding: '4px 10px',
        color: '#4caf50', fontSize: '0.78rem', fontWeight: '600',
    },
    stripeBtn: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '3px', width: '100%',
        background: 'linear-gradient(135deg, #c5a059 0%, #dbb96a 100%)',
        border: 'none', padding: '16px 24px', color: '#0a1a2a', borderRadius: '12px',
        fontSize: '1rem', fontWeight: '700', cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        boxShadow: '0 4px 16px rgba(197,160,89,0.4)',
    },
    stripeSecureNote: {
        textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '0.75rem',
    },
    featuresSection: { maxWidth: '1100px', margin: '0 auto', padding: '2rem' },
    featuresTitle: { textAlign: 'center', color: '#f5f5f5', fontSize: '1.5rem', fontWeight: '700', marginBottom: '2rem' },
    featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' },
    featureCard: {
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '16px', padding: '1.5rem', textAlign: 'center',
    },
    featureIcon: { fontSize: '2.5rem', marginBottom: '0.75rem' },
    featureTitle: { color: '#f5f5f5', fontSize: '1rem', fontWeight: '600', margin: '0 0 0.5rem 0' },
    featureDesc: { color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 },
};

export default PurchaseLicense;
